import { getProduct, getProducts, saveProduct } from '@/api/products';
import { Product, ProductState } from '@/types/product';
import { RootState } from '@/types/store';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

type FunderGetter = GetterTree<ProductState, RootState>;

const state: ProductState = {
  products: {
    data: [],
    current_page: 1,
    first_page_url: 'http://cms.test/api/products?page=1',
    from: 1,
    last_page: 1,
    last_page_url: null,
    next_page_url: null,
    path: 'http://cms.test/api/products',
    per_page: 50,
    prev_page_url: null,
    to: 50,
    total: 0,
  },
  selected: {
    id: null,
    business_id: null,
    name: null,
    logo: null,
    enabled: false,
    published: false,
    created_at: null,
    updated_at: null,
    content: {
      id: null,
      content: null,
      intro_text: null,
      costs_text: null,
      other_text: null,
      meta: null,
    },
    type: {
      name: null,
    },
    tier: {
      name: null,
    },
  },
};

const getters: FunderGetter = {
  products: state => state.products.data,
  product: state => state.selected,
  currentProductPage: state => state.products.current_page,
  totalProductPages: state => state.products.last_page,
};

const actions: ActionTree<ProductState, RootState> = {
  async getProducts({ commit }, { funder, page = 1 }): Promise<void> {
    commit('setProducts', await getProducts(funder, page));
  },

  async getProduct({ commit }, id: string) {
    commit('setSelectedProduct', await getProduct(id));
  },

  async updateProduct({ state }) {
    await saveProduct(state.selected);
  },
};

const mutations: MutationTree<ProductState> = {
  setProducts(state: ProductState, productData: ProductState): void {
    Object.assign(state.products, productData);
  },

  setSelectedProduct(state: ProductState, product: Product): void {
    Object.assign(state.selected, product);
  },

  setProductContent(productState: ProductState, content: string): void {
    productState.selected.content.content = content;
  },
  setProductIntroText(productState: ProductState, introText: string): void {
    productState.selected.content.intro_text = introText;
  },
  setProductCostsText(productState: ProductState, costsText: string): void {
    productState.selected.content.costs_text = costsText;
  },
  setProductOtherText(productState: ProductState, otherText: string): void {
    productState.selected.content.other_text = otherText;
  },

  setProductMetaTitle(productState: ProductState, title: string): void {
    productState.selected.content!.meta!.meta_title = title;
  },
  setProductMetaDescription(
    productState: ProductState,
    description: string,
  ): void {
    productState.selected.content!.meta!.meta_description = description;
  },
  setProductCanonicalUrl(
    productState: ProductState,
    canonicalUrl: string,
  ): void {
    productState.selected.content!.meta!.canonical_url = canonicalUrl;
  },
  setProductTwitterTitle(productState: ProductState, title: string): void {
    productState.selected.content!.meta!.twitter_title = title;
  },
  setProductTwitterDescription(
    productState: ProductState,
    description: string,
  ): void {
    productState.selected.content!.meta!.twitter_description = description;
  },
  setProductOpenGraphTitle(productState: ProductState, title: string): void {
    productState.selected.content!.meta!.open_graph_title = title;
  },
  setProductOpenGraphDescription(
    productState: ProductState,
    description: string,
  ): void {
    productState.selected.content!.meta!.open_graph_description = description;
  },
};

const content: Module<ProductState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true,
};

export default content;
